// AGC: useActiveTeamMemberCount — live-count active team members for the current farm
// AGC: Reads fields/{farmId}/members via the existing team API; replaces the stale usage.currentTeamMembers counter.

import { useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import { useFarms } from '../context'
import { getBackendUrl } from '../utils/backendUrl'

const BASE_URL = getBackendUrl()

interface Result {
  count: number
  isLoading: boolean
}

export const useActiveTeamMemberCount = (): Result => {
  const { getAuthToken } = useAuth()
  const { currentFarm } = useFarms()
  const [count, setCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!currentFarm?.id) return
    let cancelled = false
    setIsLoading(true)
    ;(async () => {
      try {
        const token = await getAuthToken()
        if (!token) return
        const res = await fetch(`${BASE_URL}/api/v1/farms/${currentFarm.id}/team`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const data = await res.json()
        // AGC: Only real member docs count toward the subscription seat limit (virtual owner row is excluded)
        const active = (data.members || []).filter(
          (m: { status?: string; role?: string }) => m.status === 'active' && m.role !== 'owner',
        ).length
        if (!cancelled) setCount(active)
      } catch {
        // AGC: Silent — dashboard falls back to the usage doc value
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentFarm?.id, getAuthToken])

  return { count, isLoading }
}

export default useActiveTeamMemberCount
